/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import Invoice from '../../models/invoiceModel';
import {
    AccessKey,
    AccountProgress,
    AccountStats,
    ActivateHandle,
    CompleteInvoiceFilter,
    CustomerType,
    DashboardPeriod,
    IdleState,
    InvoiceFilter,
    InvoiceRequestStatus,
    InvoiceStatus,
    InvoiceType,
    InvoiceUploadStats,
    OrderStatus,
    PagerType
} from '../../util/interfaces';
import {
    copyToClipboard,
    directTimeout,
    downloadFile,
    getSearchParams,
    IndexedObject,
    Link,
    navigate,
    nullify,
    ResponseData,
    showDialog,
    showSnackbar,
    stopPropagation,
    useEvent
} from '@dvrd/dvr-controls';
import {AppContext} from '../misc/appContext';
import {
    bulkSequence,
    downloadInvoice,
    downloadInvoices,
    emailStepsAreEnabled,
    getInvoiceRequestStats,
    getInvoiceStats,
    sendInvoices,
    uploadInvoice
} from '../../util/requestSender';
import Dashboard from './dashboard';
import {clearNavigationParam, setNavigationParam} from '../../util/navigateUtil';
import {onConfirmCheckout, onInvoicePreview, onSetCrumbs} from '../../util/events';
import {sendGaEvent, sendGaPageView} from '../../util/analytics';
import {createErrorMessage, warnTwoFactor} from '../../util/utils';
import {hasAdminLogin} from '../../util/adminUtil';
import AccountStatusChecker from '../account/pinger/statusPinger';
import {deleteFromLocal, saveToLocal} from '../../util/storageUtil';
import ReverseChargeController from '../reverseCharge/reverseChargeController';
import FirstImportController from './firstImport/firstImportController';
import AssignInvoiceNumberController from './invoiceNumber/assignInvoiceNumberController';
import {onConnectBolOauth} from '../../util/bolOauthUtil';
import User from '../../models/userModel';
import SendInvoiceController from '../invoice/send/sendInvoiceController';
import {useIsFirstRender} from '../../util/componentUtil';
import {SELECTED_ACCOUNT_KEY, SENDING_HISTORY_MAILS_KEY} from '../../util/constants';
import Account from '../../models/accountModel';
import {useLocation} from 'react-router';
import {getOpenInvoiceRequestCount} from '../../util/invoiceRequestUtil';
import useRight from '../misc/useRight';
import {useIdleObserver} from '../misc/idleObserver';
import {useTranslation} from 'react-i18next';

const DEBOUNCE = 1000;
const DEFAULT_LIMIT = 10;
const LOCAL_STORAGE_ORDER_STATUS = 'bolmate-order-status-filter';
const emptyStats: AccountStats = {
    review_count_thirty_days: 0,
    mail_count_thirty_days: 0,
    order_count_thirty_days: 0,
    mail_count: 0,
    review_count: 0,
    order_count: 0
};

function getEmptyUploadStats(): InvoiceUploadStats {
    return {
        open: getOpenInvoiceRequestCount(),
        closed: {total: 0, thirty_days: 0},
        failed: 0
    };
}

export enum DashboardTab {
    INVOICE_REQUESTS,
    INVOICES
}

function determineTab(pathname: string): DashboardTab {
    if (pathname.endsWith('aanvragen')) return DashboardTab.INVOICE_REQUESTS;
    return DashboardTab.INVOICES;
}

export default function DashboardController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {t} = useTranslation();
    const [canSeeStats] = useRight(AccessKey.INVOICE_STATS);
    const location = useLocation();
    const [page, setPage] = useState(0);
    const [invoices, setInvoices] = useState<Array<Invoice>>([]);
    const [stats, setStats] = useState<AccountStats>(emptyStats);
    const [loadingStats, setLoadingStats] = useState(false);
    const [uploadStats, setUploadStats] = useState<InvoiceUploadStats>(getEmptyUploadStats());
    const [loadingUploadStats, setLoadingUploadStats] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [allSelected, setAllSelected] = useState(false);
    const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
    const [filterOpen, setFilterOpen] = useState(false);
    const [filter, setFilter] = useState<InvoiceFilter>({});
    const [emailsEnabled, setEmailsEnabled] = useState(false);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [setupProgress, setSetupProgress] = useState<AccountProgress>(
        account?.setupProgress ?? {
            api: true,
            vat: true,
            mail: true,
            pdf_layout: true,
            upload: true
        }
    );
    const [isImportingHistory, setIsImportingHistory] = useState(false);
    const [sendingHistory, setSendingHistory] = useState(account?.getFlex(SENDING_HISTORY_MAILS_KEY) ?? false);
    const [tab, setTab] = useState<DashboardTab>(determineTab(location.pathname));
    const loadTimer = useRef<number | undefined>(undefined);
    const searchDebounce = useRef<number | null>(null);
    const reverseChargeRef = useRef<ActivateHandle>(null);
    const firstImportRef = useRef<FirstImportController>(null);
    const invNumRef = useRef<ActivateHandle>(null);
    const sendRef = useRef<ActivateHandle>(null);
    const invoiceAbortController = useRef<AbortController | null>(null);
    const statsAbortController = useRef<AbortController | null>(null);
    const hasAPIConnection = useMemo(() => !!account?.apiKeys.length, [account]);
    const isFirstRender = useIsFirstRender();
    useEvent('onResetDashboard', onReset);
    useEvent('updateInvoiceRequestCount', updateOpenRequestCount);
    useIdleObserver(onWakeStateChange);

    function onWakeStateChange(currentState: IdleState) {
        if (currentState === IdleState.IDLE) clearTimeouts();
        else loadInvoices(false);
    }

    function updateOpenRequestCount(count: number) {
        setUploadStats({...uploadStats, open: count});
    }

    function onCopyInvoiceID(invoice: Invoice) {
        return function () {
            copyToClipboard(invoice.id).then(() => {
                showSnackbar(`'${invoice.id}' ${t('dashboard.copied', 'gekopieerd')}`);
            });
        };
    }

    function onLocationChange() {
        const {pathname} = location;
        if (pathname.endsWith('aanvragen')) {
            setTab(DashboardTab.INVOICE_REQUESTS);
        } else if (pathname.endsWith('orders')) setTab(DashboardTab.INVOICES);
    }

    function onCheckTodo(key: keyof AccountProgress) {
        return function (checked: boolean) {
            const progress = Object.assign({}, setupProgress, {[key]: checked});
            updateProgress(progress);
        };
    }

    function updateProgress(progress: AccountProgress) {
        if (!account) return;
        account.setupProgress = progress;
        account.update(undefined, undefined, undefined, (account: Account, success: boolean, _: ResponseData) => {
            if (success) {
                context.onChangeContext({account});
                setSetupProgress(progress);
            }
        });
    }

    function onReset() {
        setPage(0);
        setFilter({});
        setLimit(DEFAULT_LIMIT);
        setSelectedInvoices(new Set());
        setAllSelected(false);
        cancelRequests();
        loadInvoices(true, {page: 0, filter: {}})();
        loadStats(true);
        loadUploadStats();
    }

    function onSelectInvoice(invoiceId: string) {
        return function (_: boolean, evt: React.MouseEvent) {
            stopPropagation(evt);
            const selected = new Set(selectedInvoices);
            if (selected.has(invoiceId)) selected.delete(invoiceId);
            else selected.add(invoiceId);
            setSelectedInvoices(selected);
        };
    }

    function onSelectAll() {
        setAllSelected(!allSelected);
        setSelectedInvoices(new Set());
    }

    function onClickDetails(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur details');
            changeAccountIfNeeded(invoice, () => {
                navigate(`/factuur-details?id=${invoice.id}`);
            });
        };
    }

    function onClickEdit(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur wijzigen');
            changeAccountIfNeeded(invoice, () => {
                if (invoice.invoiceType === InvoiceType.CREDIT)
                    navigate(`/credit-data?invoice-id=${invoice.referenceInvoiceId}`);
                else {
                    setNavigationParam(invoice.id);
                    navigate('/factuur-wijzigen');
                }
            });
        };
    }

    function onClickReverseCharge(invoice: Invoice) {
        return function () {
            changeAccountIfNeeded(invoice, () => {
                reverseChargeRef.current?.activate(invoice);
            });
        };
    }

    function onClickMail(invoice: Invoice | null) {
        return function () {
            if (invoice) {
                sendGaEvent('Click', 'Dashboard', 'Factuur verzenden');
                changeAccountIfNeeded(invoice, () => {
                    sendRef.current?.activate(invoice);
                });
            } else onSendSelected();
        };
    }

    function onUploadInvoice(invoice: Invoice) {
        return function () {
            setLoadingInvoices(true);
            uploadInvoice({
                data: {invoice_id: invoice.id, account_id: invoice.accountId},
                callback: (data: ResponseData) => {
                    setLoadingInvoices(false);
                    if (data.success) {
                        invoice.uploadStatus = InvoiceRequestStatus.UPLOADING;
                        onUpdateInvoice(invoice);
                        showSnackbar(t('dashboard.uploading.success', 'De factuur wordt geüpload naar Bol'));
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('dashboard.uploading.failed', 'Het uploaden van de factuur is niet gelukt.')
                            ),
                            t('dashboard.uploading.failedTitle', 'Uploaden mislukt')
                        );
                }
            });
        };
    }

    function onSendingFailed(data: IndexedObject<any>, invoice: Invoice | null) {
        const invoiceLabel = invoice
                ? t('dashboard.sending.invoiceSingular', 'factuur')
                : t('dashboard.sending.invoicePlural', 'facturen'),
            {error_type, message, order_id} = data;
        if (error_type === 'email_not_available') {
            showDialog(
                <label>
                    {t(
                        'dashboard.sending.emailNotAvailableBefore',
                        'Het originele e-mailadres van de klant is niet meer beschikbaar voor Bolmate. We raden aan om de'
                    )}{' '}
                    {invoiceLabel} {t('dashboard.sending.emailNotAvailableTe', 'te')}{' '}
                    <span className='invoice-download-link' onClick={onClickDownload(invoice)}>
                        {t('dashboard.sending.download', 'downloaden')}
                    </span>{' '}
                    {t(
                        'dashboard.sending.emailNotAvailableAfter',
                        'en deze via het Bol-platform te verzenden naar de klant.'
                    )}
                </label>,
                t('dashboard.sending.failedTitle', 'Verzenden mislukt')
            );
        } else if (error_type === 'invoice_num_not_set') {
            if (invoice)
                showDialog(
                    t(
                        'dashboard.sending.invoiceNumNotSet',
                        'Deze factuur heeft nog geen factuurnummer en kan daarom niet verzonden worden. Het systeem kan geen factuurnummer bepalen voor orders die ouder zijn dan jouw Bolmate account. Voor deze orders dien je dus zelf het factuurnummer in te stellen.'
                    ),
                    t('dashboard.sending.setInvoiceNumTitle', 'Factuurnummer instellen'),
                    [
                        {
                            label: t('dashboard.sending.setInvoiceNumTitle', 'Factuurnummer instellen'),
                            onClick: onClickAssignInvoiceNumber(invoice)
                        },
                        {label: t('dashboard.close', 'Sluiten'), primary: !invoice}
                    ]
                );
            else
                showDialog(
                    `${t('dashboard.sending.orderPrefix', 'Order')} ${order_id} ${t(
                        'dashboard.sending.invoiceNumNotSetPlural',
                        'heeft nog geen factuurnummer en kan daarom niet verzonden worden. Het systeem kan geen factuurnummer bepalen voor orders die ouder zijn dan jouw Bolmate account. Voor deze orders dien je dus zelf het factuurnummer in te stellen.'
                    )}`,
                    t('dashboard.sending.setInvoiceNumTitle', 'Factuurnummer instellen')
                );
        } else
            showDialog(
                createErrorMessage(
                    message ??
                        `${t('dashboard.sending.failedPrefix', 'Het verzenden van de')} ${invoiceLabel} ${t('dashboard.sending.failedSuffix', 'is niet gelukt.')}`
                ),
                t('dashboard.sending.failedTitle', 'Verzenden mislukt')
            );
    }

    function onClickDownload(invoice: Invoice | null) {
        return function () {
            if (!invoice) onDownloadSelected();
            else {
                sendGaEvent('Click', 'Dashboard', 'Factuur downloaden');
                changeAccountIfNeeded(invoice, () => {
                    setLoadingInvoices(true);
                    invoice.download(
                        (
                            invoice: Invoice,
                            blob: Blob | null,
                            success: boolean,
                            data: ResponseData,
                            response: Response
                        ) => {
                            setLoadingInvoices(false);
                            if (success && blob !== null) {
                                loadInvoices(false)();
                                const header = response.headers.get('Content-Disposition');
                                let fileName = `${invoice.reference}.pdf`;
                                if (header)
                                    fileName = header.substring(
                                        header.indexOf('filename=') + 'filename='.length,
                                        header.length
                                    );
                                downloadFile(blob, fileName);
                            } else {
                                if (!data.handled)
                                    showDialog(
                                        createErrorMessage(
                                            data.message ??
                                                t(
                                                    'dashboard.download.invoiceFailed',
                                                    'Het downloaden van de factuur is niet gelukt.'
                                                )
                                        ),
                                        t('dashboard.download.failedTitle', 'Downloaden mislukt')
                                    );
                            }
                        }
                    );
                });
            }
        };
    }

    function onDownloadSelected() {
        if (allSelected && !hasAdminLogin()) return onDownloadAll();
        const selectedParam: string[] | null | 'all' = allSelected
            ? 'all'
            : selectedInvoices.size
              ? Array.from(selectedInvoices)
              : null;
        if (!selectedParam || !account) return;
        setLoadingInvoices(true);
        downloadInvoices({
            data: {invoice_ids: selectedParam, account_id: account.id},
            callback: (data: ResponseData, response: Response) => {
                setLoadingInvoices(false);
                if (data.status === 'success') {
                    // FILE RESPONSE
                    loadInvoices(false)();
                    const header = response.headers.get('Content-Disposition');
                    let fileName = 'bolmate-facturen.zip';
                    if (header)
                        fileName = header.substring(header.indexOf('filename=') + 'filename='.length, header.length);
                    response.blob().then(blob => {
                        downloadFile(blob, fileName);
                    });
                } else if (!data.handled) {
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('dashboard.download.invoicesFailed', 'Het downloaden van de facturen is niet gelukt.')
                        ),
                        t('dashboard.download.failedTitle', 'Downloaden mislukt')
                    );
                }
            }
        });
    }

    function onDownloadAll() {
        showDialog(
            <label>
                {t('dashboard.downloadAll.bodyBefore', 'Je kunt alle facturen downloaden via de')}{' '}
                <Link route='/instellingen/exporteren'>
                    {t('dashboard.downloadAll.settingsExport', 'Instellingen > Exporteren')}
                </Link>
            </label>,
            t('dashboard.downloadAll.title', 'Alle facturen downloaden')
        );
    }

    function onSendSelected() {
        const selectedParam: string[] | 'all' | null = allSelected
            ? 'all'
            : selectedInvoices.size
              ? Array.from(selectedInvoices)
              : null;
        if (!selectedParam) return;
        setLoadingInvoices(true);
        if (!account) return;
        sendInvoices({
            data: {invoice_ids: selectedParam, account_id: account.id},
            callback: (data: ResponseData) => {
                setLoadingInvoices(false);
                if (data.success) {
                    showSnackbar(
                        t('dashboard.sending.scheduledWithinHour', 'De facturen worden binnen het uur verzonden')
                    );
                    loadInvoices(false)();
                    loadStats(false);
                } else if (!data.handled) onSendingFailed(data, null);
            }
        });
    }

    function onPreviewFile(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur preview');
            changeAccountIfNeeded(invoice, () => {
                setLoadingInvoices(true);
                downloadInvoice({
                    invoice_id: invoice.id,
                    account_id: invoice.accountId,
                    noHistory: true,
                    callback: (data: ResponseData, response: Response) => {
                        setLoadingInvoices(false);
                        if (data.status === 'success')
                            // FILE RESPONSE
                            response.blob().then((blob: Blob) => {
                                onInvoicePreview(URL.createObjectURL(blob));
                            });
                        else if (!data.handled)
                            showDialog(
                                createErrorMessage(data.message ?? 'Het downloaden van de factuur is niet gelukt.'),
                                'Downloaden mislukt'
                            );
                    }
                });
            });
        };
    }

    function onChangePage(page: number, pagerType: PagerType) {
        return function () {
            sendPagerEvent(pagerType, page);
            const nextPage = page - 1;
            setPage(nextPage);
            setFilterUrl({page: nextPage});
            cancelRequests();
            loadInvoices(true, {page: nextPage})();
        };
    }

    function onReload() {
        cancelRequests();
        loadInvoices()();
    }

    function onChangeSearch(value: string) {
        const sanitizedValue: string | null = nullify(value);
        // Stop if sanitized value is same as current value
        if (filter.order_id === sanitizedValue) return;
        clearDebounce();
        const invoiceFilter: InvoiceFilter = {order_id: sanitizedValue};
        setFilter(invoiceFilter);
        searchDebounce.current = window.setTimeout(() => {
            sendGaEvent('Search', 'Dashboard', 'Zoeken');
            cancelRequests();
            setPage(0);
            setFilterUrl({page: 0, filter: invoiceFilter});
            loadInvoices(true, {page: 0, filter: invoiceFilter})();
        }, DEBOUNCE);
    }

    function onChangeLimit(limit: number) {
        setLimit(limit);
        setFilterUrl({limit});
        loadInvoices(true, {limit})();
    }

    function onClickMailSettings(evt: React.MouseEvent) {
        stopPropagation(evt);
        sendGaEvent('Click', 'Dashboard', 'Automatisch mailen');
        navigate('/emails');
    }

    function onClickUpload(evt: React.MouseEvent) {
        stopPropagation(evt);
        sendGaEvent('Click', 'Dashboard', 'Automatisch uploaden');
        navigate('/facturen/aanvragen');
    }

    function onClickUploadSettings() {
        sendGaEvent('Click', 'Dashboard', 'Automatisch uploaden instellingen');
        navigate('/facturen/aanvragen?tab=settings');
    }

    function onClickApiFlow(evt: React.MouseEvent) {
        sendGaEvent('Click', 'Dashboard', 'API keys instellen');
        if (!account) return;
        saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
        onConnectBolOauth(evt.shiftKey);
    }

    function onClickInvoice(evt: React.MouseEvent) {
        stopPropagation(evt);
        onCheckTodo('pdf_layout')(true);
        navigate('/factuur-layout');
    }

    function onClickVatSettings(evt: React.MouseEvent) {
        stopPropagation(evt);
        onCheckTodo('vat')(true);
        navigate('/instellingen/producten');
    }

    function onClickCredit(invoice: Invoice) {
        return function () {
            changeAccountIfNeeded(invoice, () => {
                if (invoice.invoiceType === InvoiceType.CREDIT) {
                    setNavigationParam(invoice);
                    navigate('/factuur-details');
                } else if (invoice.hasCreditInvoice) {
                    clearNavigationParam();
                    navigate(`/factuur-details?id=${invoice.creditInvoiceId}`);
                } else navigate(`/credit-data?invoice-id=${invoice.id}`);
            });
        };
    }

    function onOpenFilters() {
        setFilterOpen(true);
    }

    function onCloseFilters() {
        setFilterOpen(false);
    }

    function onSubmitFilters(filters: CompleteInvoiceFilter) {
        onCloseFilters();
        onChangeOrderStatus(filters.order_status)();
        setFilter(filters);
        setPage(0);
        setFilterUrl({filter: filters});
        loadInvoices(true, {filter: filters, page: 0})();
    }

    function onChangeOrderStatus(orderStatus: OrderStatus | '' | null) {
        return function () {
            orderStatus = nullify(orderStatus);
            if (orderStatus) saveToLocal(LOCAL_STORAGE_ORDER_STATUS, orderStatus);
            else deleteFromLocal(LOCAL_STORAGE_ORDER_STATUS);
        };
    }

    function onBulkApplyEmailSequence() {
        if (!account) return;
        if (!selectedInvoices.size)
            return showDialog(
                t(
                    'dashboard.bulk.selectionRequired',
                    'Het is alleen mogelijk om dit toe te passen voor specifiek geselecteerde facturen.'
                )
            );
        setLoadingInvoices(true);
        bulkSequence({
            data: {invoice_ids: Array.from(selectedInvoices), account_id: account.id},
            callback: (data: ResponseData) => {
                setLoadingInvoices(false);
                if (data.success) showSnackbar(t('dashboard.bulk.emailsSent', 'De e-mails zijn verzonden'));
                else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('dashboard.bulk.emailsFailed', 'Het verzenden van de e-mails is niet gelukt.')
                        ),
                        t('dashboard.sending.failedTitle', 'Verzenden mislukt')
                    );
            }
        });
    }

    function onFirstOldImport() {
        const rows = document.querySelectorAll('div.invoices div.row');
        if (!rows || !firstImportRef.current) return;
        const firstRow = Array.from(rows).find((row: Element) => !row.classList.contains('head'));
        if (!firstRow) return;
        firstRow.classList.toggle('first-order', true);
        // Force DOM manipulation before activation
        directTimeout(() => {
            firstImportRef.current?.onActivate();
        });
    }

    function onClickAssignInvoiceNumber(invoice: Invoice) {
        return function (evt?: React.MouseEvent) {
            if (evt) stopPropagation(evt);
            changeAccountIfNeeded(invoice, () => {
                invNumRef.current?.activate(invoice);
            });
        };
    }

    function onCancelEmails(invoice: Invoice) {
        return function () {
            showDialog(
                t(
                    'dashboard.cancelEmails.confirmBody',
                    'Weet je zeker dat je geen automatische mails voor deze bestelling meer wilt verzenden?'
                ),
                t('dashboard.cancelEmails.title', 'Mails annuleren'),
                [
                    t('common.no', 'Nee'),
                    {label: t('common.yes', 'Ja'), onClick: onConfirmCancelEmails(invoice), primary: false}
                ]
            );
        };
    }

    function onConfirmCancelEmails(invoice: Invoice) {
        return function () {
            setLoadingInvoices(true);
            changeAccountIfNeeded(invoice, () => {
                invoice.cancelEmails((invoice: Invoice, success: boolean, data: ResponseData) => {
                    setLoadingInvoices(false);
                    if (success) {
                        onUpdateInvoice(invoice);
                        showSnackbar(
                            t('dashboard.cancelEmails.success', 'Mails voor deze factuur worden niet meer verzonden')
                        );
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('dashboard.cancelEmails.failed', 'Het annuleren van de mails is niet gelukt')
                            ),
                            t('dashboard.cancelEmails.failedTitle', 'Annuleren mislukt')
                        );
                });
            });
        };
    }

    function onUpdateInvoice(invoice: Invoice) {
        const _invoices = invoices.slice();
        for (let i = 0; i < _invoices.length; i++) {
            if (_invoices[i].id === invoice.id) {
                _invoices.splice(i, 1, invoice);
                break;
            }
        }
        setInvoices(_invoices);
    }

    function onSendSuccess(invoice: Invoice) {
        onUpdateInvoice(invoice);
        loadStats(false);
    }

    function onDismissImportInfo() {
        if (!user) return;
        setLoadingInvoices(true);
        user.setFlex('import_info_shown', true);
        user.update(undefined, (user: User) => {
            setLoadingInvoices(false);
            context.onChangeContext({user: user.clone()});
        });
    }

    function changeAccountIfNeeded(invoice: Invoice, callback: VoidFunction) {
        const {account, accounts} = context.accountContext;
        if (invoice.accountId === account?.id) return callback();
        const invoiceAccount = accounts.find((account: Account) => account.id === invoice.accountId);
        if (invoiceAccount) context.onChangeContext({account: invoiceAccount}, callback);
    }

    function setFilterUrl(params?: {filter?: Partial<InvoiceFilter>; page?: number; limit?: number}) {
        const _filter = params?.filter ?? filter;
        const _page = params?.page ?? page;
        const _limit = params?.limit ?? limit;
        const {origin, pathname} = window.location;
        let url = `${origin}${pathname}?page=${_page + 1}&limit=${_limit}`;
        if (_filter.order_id) url += `&order-id=${_filter.order_id}`;
        if (_filter.status) url += `&status=${_filter.status}`;
        if (_filter.order_status) url += `&order-status=${_filter.order_status}`;
        if (_filter.country) url += `&land=${_filter.country}`;
        if (_filter.firstname) url += `&voornaam=${encodeURIComponent(_filter.firstname)}`;
        if (_filter.lastname) url += `&achternaam=${encodeURIComponent(_filter.lastname)}`;
        if (_filter.invoice_type) url += `&factuur-soort=${_filter.invoice_type}`;
        if (_filter.company) url += `&bedrijf=${encodeURIComponent(_filter.company)}`;
        if (_filter.reference) url += `&referentie=${encodeURIComponent(_filter.reference)}`;
        if (_filter.vat_number) url += `&btw=${encodeURIComponent(_filter.vat_number)}`;
        if (_filter.coc_number) url += `&kvk=${encodeURIComponent(_filter.coc_number)}`;
        if (_filter.customer_type) url += `&klant-type=${_filter.customer_type}`;
        if (_filter.invoice_number) url += `&factuur-nummer=${encodeURIComponent(_filter.invoice_number)}`;
        history.pushState({}, '', url);
    }

    function sendPagerEvent(pagerType: PagerType, page: number) {
        switch (pagerType) {
            case PagerType.FIRST:
                sendGaEvent('Click', 'Dashboard', 'Eerste pagina');
                break;
            case PagerType.LAST:
                sendGaEvent('Click', 'Dashboard', 'Laatste pagina');
                break;
            case PagerType.NEXT:
                sendGaEvent('Click', 'Dashboard', 'Volgende pagina');
                break;
            case PagerType.PAGE:
                sendGaEvent('Click', 'Dashboard', `Pagina ${page}`);
                break;
            case PagerType.PREVIOUS:
                sendGaEvent('Click', 'Dashboard', 'Vorige pagina');
                break;
        }
    }

    function loadData(accountChanged: boolean = false) {
        if (accountChanged) {
            setPage(0);
            setFilter({});
            setFilterUrl({filter: {}, page: 0});
            setSelectedInvoices(new Set());
            setAllSelected(false);
            loadInvoices(accountChanged, {page: 0, filter: {}})();
            loadStats(accountChanged);
            loadUploadStats();
        }
    }

    function prepareFilters(filters?: InvoiceFilter): InvoiceFilter {
        if (!filters) return {};
        for (const key of Object.keys(filters)) filters[key] = nullify(filters[key]);
        return filters;
    }

    function loadInvoices(
        load: boolean = true,
        params?: {
            filter?: InvoiceFilter;
            page?: number;
            period?: DashboardPeriod;
            limit?: number;
        }
    ) {
        return function () {
            clearDebounce();
            if (loadingInvoices) return;
            if (!account) return;
            const _filter = params?.filter ?? filter;
            const _page = params?.page ?? page;
            const _limit = params?.limit ?? limit;
            if (load) setLoadingInvoices(true);
            const accountId = account.id;
            const filters = prepareFilters(_filter);
            invoiceAbortController.current = Invoice.getAll(
                accountId,
                filters,
                _limit,
                _page,
                (invoices: Invoice[], success: boolean, data: ResponseData) => {
                    invoiceAbortController.current = null;
                    setLoadingInvoices(false);
                    if (!success) return;
                    const {
                        imported_from_api,
                        previously_imported_old_order,
                        is_importing,
                        account,
                        sending_history_mails
                    } = data;
                    setInvoices(invoices);
                    updateContext(new Account(account));
                    setIsImportingHistory(is_importing);
                    setSendingHistory(sending_history_mails);
                    if (imported_from_api) {
                        if (!previously_imported_old_order) window.setTimeout(onFirstOldImport);
                        else if (invoices.length === 1) {
                            onClickAssignInvoiceNumber(invoices[0])();
                        }
                    }
                },
                () => {
                    invoiceAbortController.current = null;
                    setLoadingInvoices(false);
                }
            );
        };
    }

    function updateContext(nextAccount: Account) {
        if (account?.historyImported === nextAccount.historyImported) return;
        context.onChangeContext({account: nextAccount});
    }

    function loadStats(load: boolean = true) {
        if (!account || !canSeeStats) return;
        if (load) setLoadingStats(true);
        statsAbortController.current = getInvoiceStats({
            data: {
                account_id: account.id
            },
            callback: (data: ResponseData) => {
                statsAbortController.current = null;
                setLoadingStats(false);
                if (!data.success) return;
                const {
                    mail_count,
                    review_count,
                    order_count,
                    order_count_thirty_days,
                    mail_count_thirty_days,
                    review_count_thirty_days
                } = data;
                setStats({
                    mail_count,
                    order_count,
                    review_count,
                    order_count_thirty_days,
                    mail_count_thirty_days,
                    review_count_thirty_days
                });
            },
            onAbort: () => {
                statsAbortController.current = null;
                setLoadingStats(false);
            }
        });
    }

    function loadUploadStats() {
        if (!account) return;
        setLoadingUploadStats(true);
        getInvoiceRequestStats({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoadingUploadStats(false);
                if (data.success) setUploadStats(Object.assign({}, uploadStats, data.stats));
            }
        });
    }

    function loadEmailsEnabled() {
        if (!account) return;
        emailStepsAreEnabled({
            account_id: account.id,
            callback: (data: ResponseData) => {
                const {success, are_enabled} = data;
                if (success) setEmailsEnabled(are_enabled);
            }
        });
    }

    function clearDebounce() {
        if (searchDebounce.current !== null) {
            window.clearTimeout(searchDebounce.current);
            searchDebounce.current = null;
        }
    }

    function processParams() {
        const params = getSearchParams(location.search);
        const filters: InvoiceFilter = getParamsFilters(params);
        let page: number = 0;
        let _limit = limit;
        if (params.has('page')) page = parseInt(params.get('page') || '1') - 1;
        if (params.has('subscription') && params.get('subscription') === '1') {
            window.history.pushState({}, document.title, window.location.pathname);
            onConfirmCheckout();
        }
        if (params.has('limit')) _limit = parseInt(params.get('limit')!);
        setLimit(_limit);
        setPage(page);
        setFilter(filters);
        loadInvoices(true, {filter: filters, page, limit: _limit})();
    }

    function getParamsFilters(params: URLSearchParams): InvoiceFilter {
        return {
            order_id: getParam(params, 'order-id'),
            coc_number: getParam(params, 'kvk', true),
            order_status: getOrderStatus(params),
            status: getStatus(params),
            invoice_type: getInvoiceType(params),
            reference: getParam(params, 'referentie', true),
            vat_number: getParam(params, 'btw', true),
            company: getParam(params, 'bedrijf', true),
            lastname: getParam(params, 'achternaam', true),
            firstname: getParam(params, 'voornaam', true),
            country: getParam(params, 'land', true),
            customer_type: (getParam(params, 'klant-type') as CustomerType | null) ?? CustomerType.BOTH,
            invoice_num: getParam(params, 'factuur-nummer', true)
        };
    }

    function getParam(params: URLSearchParams, paramName: string, decode: boolean = false): string | null {
        const value = nullify(params.get(paramName));
        if (!value) return null;
        return decode ? decodeURIComponent(value) : value;
    }

    function getInvoiceType(params: URLSearchParams): InvoiceType | null {
        const invoiceType = params.get('factuur-soort');
        if (invoiceType === InvoiceType.DEBET) return InvoiceType.DEBET;
        else if (invoiceType === InvoiceType.CREDIT) return InvoiceType.CREDIT;
        return null;
    }

    function getStatus(params: URLSearchParams): InvoiceStatus | null {
        const status = params.get('status');
        if (status === InvoiceStatus.SENT) return InvoiceStatus.SENT;
        else if (status === InvoiceStatus.WARNING) return InvoiceStatus.WARNING;
        else if (status === InvoiceStatus.ERROR) return InvoiceStatus.ERROR;
        else if (status === InvoiceStatus.TRANSPORT) return InvoiceStatus.TRANSPORT;
        else if (status === InvoiceStatus.IMPORTED) return InvoiceStatus.IMPORTED;
        return null;
    }

    function getOrderStatus(params: URLSearchParams): OrderStatus | null {
        const orderStatus = params.get('order-status');
        if (orderStatus === OrderStatus.CANCELED) return OrderStatus.CANCELED;
        else if (orderStatus === OrderStatus.SENT) return OrderStatus.SENT;
        else if (orderStatus === OrderStatus.PLACED) return OrderStatus.PLACED;
        return null;
    }

    function cancelRequests() {
        invoiceAbortController.current?.abort();
        statsAbortController.current?.abort();
    }

    function clearTimeouts() {
        window.clearTimeout(loadTimer.current);
        loadTimer.current = undefined;
    }

    useEffect(() => {
        if (tab === DashboardTab.INVOICES)
            onSetCrumbs([{label: t('dashboard.crumbs.invoices', 'Facturen'), route: '/facturen/orders'}]);
        else if (tab === DashboardTab.INVOICE_REQUESTS)
            onSetCrumbs([
                {label: t('dashboard.crumbs.invoiceRequests', 'Factuuraanvragen'), route: '/facturen/aanvragen'}
            ]);
    }, [tab, t]);

    useEffect(() => {
        sendGaPageView('/facturen');
        if (!user) navigate('/inloggen');
        else warnTwoFactor(user);
        processParams();
        loadStats();
        loadUploadStats();
    }, []);

    useEffect(() => {
        loadEmailsEnabled();
        setFilter({});
        if (!isFirstRender) window.setTimeout(() => loadData(true));
    }, [account?.historyImported, account?.id]);

    useEffect(() => {
        onLocationChange();
    }, [location.pathname]);

    useEffect(() => {
        if (account) setSetupProgress(account.setupProgress);
    }, [account?.setupProgress]);

    useEffect(() => {
        let delay: number;
        if (isImportingHistory)
            delay = 1000; // Orders might appear anytime, fast reload
        else if (sendingHistory)
            delay = 5000; // Sending might finish anytime, semi fast reload
        else return; // Don't auto reload
        loadTimer.current = window.setTimeout(loadInvoices(false), delay);

        return function () {
            clearTimeouts();
        };
    }, [isImportingHistory, sendingHistory, loadInvoices, invoices]);

    return (
        <>
            <Dashboard
                onClickEdit={onClickEdit}
                onClickMail={onClickMail}
                emailsEnabled={emailsEnabled}
                onCheckTodo={onCheckTodo}
                onClickInvoice={onClickInvoice}
                onClickDownload={onClickDownload}
                onClickVatSettings={onClickVatSettings}
                onChangePage={onChangePage}
                filter={filter}
                onChangeSearch={onChangeSearch}
                onSearch={loadInvoices()}
                page={page + 1}
                invoices={invoices}
                stats={stats}
                onCloseFilters={onCloseFilters}
                loadingInvoices={loadingInvoices}
                loadingStats={loadingStats}
                limit={limit}
                onPreviewFile={onPreviewFile}
                onClickMailSettings={onClickMailSettings}
                onClickApiFlow={onClickApiFlow}
                allSelected={allSelected}
                onChangeLimit={onChangeLimit}
                selectedInvoices={Array.from(selectedInvoices)}
                onSelectAll={onSelectAll}
                filtersOpen={filterOpen}
                onSelectInvoice={onSelectInvoice}
                onClickDetails={onClickDetails}
                onClickCredit={onClickCredit}
                onSubmitFilters={onSubmitFilters}
                onClickReverseCharge={onClickReverseCharge}
                onBulkApplyEmailSequence={onBulkApplyEmailSequence}
                onOpenFilters={onOpenFilters}
                onClickAssignInvoiceNumber={onClickAssignInvoiceNumber}
                onCancelEmails={onCancelEmails}
                onDismissImportInfo={onDismissImportInfo}
                onReload={onReload}
                setupProgress={setupProgress}
                isImportingHistory={isImportingHistory}
                sendingHistory={sendingHistory}
                tab={tab}
                reloadNumOpen={loadUploadStats}
                uploadStats={uploadStats}
                loadingUploadStats={loadingUploadStats}
                onClickUpload={onClickUpload}
                onClickUploadSettings={onClickUploadSettings}
                onUploadInvoice={onUploadInvoice}
                onCopyInvoiceID={onCopyInvoiceID}
                hasAPIConnection={hasAPIConnection}
            />
            <AccountStatusChecker />
            <ReverseChargeController onSuccess={loadInvoices()} ref={reverseChargeRef} />
            <FirstImportController ref={firstImportRef} />
            <AssignInvoiceNumberController onUpdate={onUpdateInvoice} ref={invNumRef} />
            <SendInvoiceController
                onSuccess={onSendSuccess}
                onAssignInvoiceNumber={onClickAssignInvoiceNumber}
                onDownloadInvoice={onClickDownload}
                ref={sendRef}
            />
        </>
    );
}
